/**
 * Расчёт достижений и серии занятий (ТЗ FR-GM-07, FR-GM-10).
 *
 * Достижения считаются по всему пространству, а не по профилю языка: они
 * принадлежат человеку, а не его русскому или английскому навыку. Рекорды,
 * наоборот, живут в профиле — скорость на русском и на английском это разные
 * умения, и общий рекорд на двоих не значил бы ничего.
 */

import { MASTERED_THRESHOLD, TARGET_WPM, type MasteryStat } from '@type2x/shared-contracts';

import type {
  AchievementDefinition,
  AchievementId,
  AchievementStatus,
  Streak,
  UnlockedAchievement,
} from '../contracts/achievements.js';
import { ACHIEVEMENTS } from '../contracts/achievements.js';
import type { LanguageProfile, Workspace } from '../contracts/workspace.js';

/** Один день в миллисекундах. */
const DAY_MS = 86_400_000;

/** Календарная дата без времени: серия считается по дням, а не по часам. */
function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * Серия занятий по календарным датам.
 *
 * Текущая серия обрывается, если пропущен день. Вчерашнее занятие серию
 * сохраняет: человек, занимавшийся вчера вечером и открывший приложение
 * утром, серию ещё не потерял.
 */
/**
 * Серия занятий по календарным дням (ТЗ FR-EN-71).
 *
 * Три занятия за один вечер — один день практики: серия поощряет регулярность,
 * а не усидчивость в один присест. Занятие вчера серию сохраняет, позавчера —
 * обрывает.
 */
export function streakOf(days: readonly string[], todayIso: string): Streak {
  const unique = [...new Set(days.map(dayOf))].sort();
  if (unique.length === 0) return { current: 0, longest: 0, total: 0 };

  let longest = 1;
  let run = 1;

  for (let index = 1; index < unique.length; index += 1) {
    const previous = Date.parse(`${unique[index - 1]!}T00:00:00.000Z`);
    const current = Date.parse(`${unique[index]!}T00:00:00.000Z`);

    run = current - previous === DAY_MS ? run + 1 : 1;
    longest = Math.max(longest, run);
  }

  const today = Date.parse(`${dayOf(todayIso)}T00:00:00.000Z`);
  const last = Date.parse(`${unique[unique.length - 1]!}T00:00:00.000Z`);
  const gap = today - last;
  const current = gap === 0 || gap === DAY_MS ? run : 0;

  return { current, longest, total: unique.length };
}

/** Сколько единиц профиля освоено по условию. */
function masteredCount(profile: LanguageProfile, matches: (content: string) => boolean): number {
  return Object.values(profile.units).filter((unit) => {
    if (!matches(unit.content)) return false;
    const stat: MasteryStat | undefined = profile.stats[unit.id];
    return stat !== undefined && stat.masteryScore >= MASTERED_THRESHOLD;
  }).length;
}

/** Слово: два и более знака, и все они буквы. */
function isWord(content: string): boolean {
  const characters = [...content];
  return characters.length >= 2 && characters.every((character) => /\p{L}/u.test(character));
}

/** Символ: не буква и не цифра — знак препинания или специальный знак. */
function isSymbol(content: string): boolean {
  const characters = [...content];
  return characters.length === 1 && /^[^\p{L}\p{N}\s]$/u.test(content);
}

/** Достигнутое значение по каждому рубежу. */
function measure(workspace: Workspace, id: AchievementId): number {
  const profiles = Object.values(workspace.profiles);
  const sum = (pick: (profile: LanguageProfile) => number): number =>
    profiles.reduce((total, profile) => total + pick(profile), 0);
  const best = (pick: (profile: LanguageProfile) => number): number =>
    profiles.reduce((top, profile) => Math.max(top, pick(profile)), 0);

  switch (id) {
    case 'first-thousand':
      return sum((profile) => profile.typedCharacters);
    case 'first-verified':
      return best((profile) => profile.bestVerifiedWpm) > 0 ? 1 : 0;
    case 'accuracy-95':
      return best((profile) => profile.bestAccuracy);
    case 'ten-words':
      return sum((profile) => masteredCount(profile, isWord));
    case 'seven-days':
      return streakOf(
        profiles.flatMap((profile) => profile.practiceDays),
        workspace.updatedAt,
      ).total;
    case 'verified-35-wpm':
      return best((profile) => profile.bestVerifiedWpm);
    case 'symbols-confident':
      return sum((profile) => masteredCount(profile, isSymbol));
    case 'two-languages':
      // Одинаково уверенная печать на двух языках: подтверждённый результат
      // целевого уровня на каждом. Один язык, выученный вдвое лучше другого,
      // этого не заменяет.
      return profiles.filter((profile) => profile.bestVerifiedWpm >= TARGET_WPM).length;
  }
}

/** Взят ли рубеж при таком достигнутом значении. */
function reached(definition: AchievementDefinition, current: number): boolean {
  return current >= definition.target;
}

/**
 * Состояние всех рубежей: что уже получено и сколько осталось до остальных.
 *
 * Полученное берётся из пространства, а не пересчитывается: показатель мог
 * просесть, а достижение остаётся (ТЗ FR-SUB-06).
 */
export function achievementProgress(workspace: Workspace): readonly AchievementStatus[] {
  const unlocked = new Map(workspace.achievements.map((item) => [item.id, item.unlockedAt]));

  return ACHIEVEMENTS.map((definition) => ({
    id: definition.id,
    target: definition.target,
    current: measure(workspace, definition.id),
    unlockedAt: unlocked.get(definition.id) ?? null,
  }));
}

/**
 * Отмечает вновь взятые рубежи.
 *
 * Возвращает то же пространство, если ничего не изменилось: лишняя запись в
 * хранилище — это лишняя причина для расхождения.
 */
export function awardAchievements(workspace: Workspace, now: string): Workspace {
  const already = new Set(workspace.achievements.map((item) => item.id));
  const earned: UnlockedAchievement[] = ACHIEVEMENTS.filter(
    (definition) =>
      !already.has(definition.id) && reached(definition, measure(workspace, definition.id)),
  ).map((definition) => ({ id: definition.id, unlockedAt: now }));

  if (earned.length === 0) return workspace;

  return {
    ...workspace,
    achievements: [...workspace.achievements, ...earned],
    updatedAt: now,
  };
}

/** Достижения, полученные именно сейчас: их показывает экран результата. */
export function achievementsEarnedAt(workspace: Workspace, now: string): readonly AchievementId[] {
  return workspace.achievements.filter((item) => item.unlockedAt === now).map((item) => item.id);
}
